import React from 'react';
import { Card, CardContent, Grid, Skeleton, Typography } from '@mui/material';
import { ContratoKpis } from '../../../services/contratoVentaService';
import { formatCurrency } from '../../../utils/formatters';

interface ContratosKpisBarProps {
  kpis: ContratoKpis | null;
  loading?: boolean;
}

const KpiTile: React.FC<{ label: string; value: React.ReactNode; accent?: boolean }> = ({
  label,
  value,
  accent,
}) => (
  <Card
    variant="outlined"
    sx={{
      height: '100%',
      borderRadius: 2,
      borderColor: accent ? 'warning.main' : 'divider',
      bgcolor: accent ? 'warning.50' : 'background.paper',
    }}
  >
    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="h6" fontWeight={700} sx={{ mt: 0.5, fontSize: { xs: '1rem', sm: '1.15rem' } }}>
        {value}
      </Typography>
    </CardContent>
  </Card>
);

const ContratosKpisBar: React.FC<ContratosKpisBarProps> = ({ kpis, loading }) => {
  if (loading) {
    return (
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        {Array.from({ length: 6 }).map((_, i) => (
          <Grid item xs={6} sm={4} md={2} key={i}>
            <Skeleton variant="rounded" height={72} />
          </Grid>
        ))}
      </Grid>
    );
  }

  if (!kpis) return null;

  return (
    <Grid container spacing={1.5} sx={{ mb: 2 }}>
      <Grid item xs={6} sm={4} md={2}>
        <KpiTile label="Vigentes" value={kpis.contratosVigentes} />
      </Grid>
      <Grid item xs={6} sm={4} md={2}>
        <KpiTile label="Monto contratado" value={formatCurrency(kpis.montoContratadoVigente)} />
      </Grid>
      <Grid item xs={6} sm={4} md={2}>
        <KpiTile
          label="Pendiente (moneda)"
          value={formatCurrency(kpis.pendienteFacturarMoneda)}
          accent={kpis.pendienteFacturarMoneda > 0}
        />
      </Grid>
      <Grid item xs={6} sm={4} md={2}>
        <KpiTile
          label="Pendiente ($)"
          value={formatCurrency(kpis.pendienteFacturarPesos)}
          accent={kpis.pendienteFacturarPesos > 0}
        />
      </Grid>
      <Grid item xs={6} sm={4} md={2}>
        <KpiTile
          label="Períodos vencidos"
          value={kpis.periodosVencidos}
          accent={kpis.periodosVencidos > 0}
        />
      </Grid>
      <Grid item xs={6} sm={4} md={2}>
        <KpiTile
          label="Avances s/facturar"
          value={`${kpis.avancesSinFacturar} · vence 30d: ${kpis.contratosPorVencer30Dias}`}
          accent={kpis.avancesSinFacturar > 0}
        />
      </Grid>
    </Grid>
  );
};

export default ContratosKpisBar;
